import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../services/firebase';

interface ImageUploadProps {
  currentImageUrl?: string;
  onImageUploaded: (url: string) => void;
  onImageDeleted: () => void;
  storagePath: string; // e.g., "product-categories/CATEGORY_ID"
  label?: string;
  maxSizeMB?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  onImageUploaded,
  onImageDeleted,
  storagePath,
  label = '画像',
  maxSizeMB = 5,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`ファイルサイズは${maxSizeMB}MB以下にしてください`);
      return;
    }

    // ファイルタイプチェック
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      // ファイル名を生成（タイムスタンプ + オリジナルファイル名）
      const timestamp = Date.now();
      const filename = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${storagePath}/${filename}`);

      // アップロード
      await uploadBytes(storageRef, file);

      // ダウンロードURLを取得
      const downloadURL = await getDownloadURL(storageRef);

      onImageUploaded(downloadURL);
    } catch (err) {
      console.error('Upload error:', err);
      setError('画像のアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentImageUrl) return;

    if (!window.confirm('画像を削除してもよろしいですか？')) {
      return;
    }

    try {
      // URLからStorage参照を作成
      const imageRef = ref(storage, currentImageUrl);
      await deleteObject(imageRef);
      onImageDeleted();
    } catch (err) {
      console.error('Delete error:', err);
      setError('画像の削除に失敗しました');
    }
  };

  return (
    <Box>
      <Typography variant="body2" gutterBottom>
        {label}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {currentImageUrl ? (
        <Box>
          <Box
            component="img"
            src={currentImageUrl}
            alt={label}
            sx={{
              width: '100%',
              maxWidth: 300,
              height: 'auto',
              borderRadius: 1,
              mb: 1,
            }}
          />
          <Box>
            <IconButton
              onClick={handleDelete}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id={`image-upload-${storagePath}`}
            type="file"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <label htmlFor={`image-upload-${storagePath}`}>
            <Button
              variant="outlined"
              component="span"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              disabled={uploading}
            >
              {uploading ? 'アップロード中...' : '画像をアップロード'}
            </Button>
          </label>
          <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
            最大{maxSizeMB}MB、JPG/PNG形式
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ImageUpload;
